import { lazy, Suspense } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { AppShell } from './components/AppShell';
import { Skeleton } from './components/ui';

// Lazy-load pages so heavy libs (TipTap, React Flow) only load on the pages
// that use them — keeps the initial bundle small.
//
// A dynamic import can fail when a new version was deployed while this tab was
// open: the hashed chunk this build references no longer exists on the server
// (404). Nothing wraps <Suspense> in an error boundary, so that rejection would
// unmount the whole app (blank screen). Recover by forcing a single full reload
// to fetch the current index.html + chunk names; a sessionStorage guard avoids
// a reload loop if the import keeps failing for some other reason.
const RELOAD_GUARD = 'atlas.chunkReloadedAt';
const named = <T, K extends keyof T>(
  loader: () => Promise<T>,
  key: K,
) =>
  lazy(() =>
    loader()
      .then((m) => {
        sessionStorage.removeItem(RELOAD_GUARD); // a chunk loaded → re-arm recovery
        return { default: m[key] as React.ComponentType };
      })
      .catch((err) => {
        if (!sessionStorage.getItem(RELOAD_GUARD)) {
          sessionStorage.setItem(RELOAD_GUARD, String(Date.now()));
          window.location.reload();
          // Keep <Suspense> in its fallback during the brief moment before the
          // reload takes effect, rather than surfacing the error.
          return new Promise<{ default: React.ComponentType }>(() => {});
        }
        throw err;
      }),
  );

const Dashboard = named(() => import('./pages/Dashboard'), 'Dashboard');
const DevelopmentAgent = named(() => import('./pages/DevelopmentAgent'), 'DevelopmentAgent');
const MyWork = named(() => import('./pages/MyWork'), 'MyWork');
const Tickets = named(() => import('./pages/Tickets'), 'Tickets');
const AssignmentQueue = named(() => import('./pages/AssignmentQueue'), 'AssignmentQueue');
const TicketDetail = named(() => import('./pages/TicketDetail'), 'TicketDetail');
const Board = named(() => import('./pages/Board'), 'Board');
const Backlog = named(() => import('./pages/Backlog'), 'Backlog');
const Sprints = named(() => import('./pages/Sprints'), 'Sprints');
const Releases = named(() => import('./pages/Releases'), 'Releases');
const ReleaseReview = named(() => import('./pages/ReleaseReview'), 'ReleaseReview');
const Roadmap = named(() => import('./pages/Roadmap'), 'Roadmap');
const Prioritize = named(() => import('./pages/Prioritize'), 'Prioritize');
const Calendar = named(() => import('./pages/Calendar'), 'Calendar');
const Docs = named(() => import('./pages/Docs'), 'Docs');
const Reports = named(() => import('./pages/Reports'), 'Reports');
const ReportDetail = named(() => import('./pages/ReportDetail'), 'ReportDetail');
const Dashboards = named(() => import('./pages/Dashboards'), 'Dashboards');
const DashboardDetail = named(() => import('./pages/DashboardDetail'), 'DashboardDetail');
const Admin = named(() => import('./pages/Admin'), 'Admin');
const Customers = named(() => import('./pages/Customers'), 'Customers');
const CustomerProfile = named(() => import('./pages/CustomerProfile'), 'CustomerProfile');
const FeatureRequests = named(() => import('./pages/FeatureRequests'), 'FeatureRequests');
const ThirdPartyRisk = named(() => import('./pages/ThirdPartyRisk'), 'ThirdPartyRisk');
const Infrastructure = named(() => import('./pages/Infrastructure'), 'Infrastructure');
const Notifications = named(() => import('./pages/Notifications'), 'Notifications');
const NotificationSettings = named(() => import('./pages/NotificationSettings'), 'NotificationSettings');
const MorningBrief = named(() => import('./pages/MorningBrief'), 'MorningBrief');
const TranscriptWizard = named(() => import('./pages/TranscriptWizard'), 'TranscriptWizard');
const Profile = named(() => import('./pages/Profile'), 'Profile');
const Search = named(() => import('./pages/Search'), 'Search');
const Workspaces = named(() => import('./pages/Workspaces'), 'Workspaces');
const TeamCourt = named(() => import('./features/balls/TeamCourt'), 'TeamCourt');

import { TicketStatusProvider } from './lib/ticket-status-config';

export default function App() {
  const location = useLocation();
  return (
    <TicketStatusProvider><AppShell>
      <Suspense fallback={<Skeleton className="h-96" />}>
        <AnimatePresence mode="wait">
          <Routes location={location} key={location.pathname}>
            <Route path="/" element={<Dashboard />} />
            <Route path="/workspaces/:kind" element={<Workspaces />} />
            <Route path="/my-work" element={<MyWork />} />
            <Route path="/team-court" element={<TeamCourt />} />
            <Route path="/development-agent" element={<DevelopmentAgent />} />
            <Route path="/tickets" element={<Tickets key={new URLSearchParams(location.search).get('department') ?? 'all'} />} />
            <Route path="/assignment" element={<AssignmentQueue />} />
            <Route path="/tickets/:id" element={<TicketDetail />} />
            <Route path="/board" element={<Board />} />
            <Route path="/board/:kind" element={<Board />} />
            <Route path="/backlog" element={<Backlog />} />
            <Route path="/sprints" element={<Sprints />} />
            <Route path="/releases" element={<Releases />} />
            <Route path="/releases/:id/review" element={<ReleaseReview />} />
            <Route path="/transcript-wizard" element={<TranscriptWizard />} />
            <Route path="/roadmap" element={<Roadmap />} />
            <Route path="/prioritize" element={<Prioritize />} />
            <Route path="/calendar" element={<Calendar />} />
            <Route path="/docs/*" element={<Docs />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/reports/new" element={<ReportDetail />} />
            <Route path="/reports/:id" element={<ReportDetail />} />
            <Route path="/dashboards" element={<Dashboards />} />
            <Route path="/dashboards/:id" element={<DashboardDetail />} />
            <Route path="/customers" element={<Customers />} />
            <Route path="/customers/:id" element={<CustomerProfile />} />
            <Route path="/feature-requests" element={<FeatureRequests />} />
            <Route path="/third-party-risk" element={<ThirdPartyRisk />} />
            <Route path="/infrastructure" element={<Infrastructure />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/settings/notifications" element={<NotificationSettings />} />
            <Route path="/morning-brief" element={<MorningBrief />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/search" element={<Search />} />
            <Route path="*" element={<div className="text-muted">Not found.</div>} />
          </Routes>
        </AnimatePresence>
      </Suspense>
    </AppShell></TicketStatusProvider>
  );
}
